import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChangeLog, Pagination, Response } from '@ui/src';
import {
  Organization,
  TransactionMonitoringRule,
  TransactionMonitoringRuleFormValues,
} from '../../../types';
import { transactionMonitoringRulesApi } from '../services';

export interface TransactionMonitorinRuleState {
  isLoading: boolean;
  formValues?: TransactionMonitoringRuleFormValues;
  requestInProgress: boolean;
  submitSuccess?: boolean;
  ruleId?: string;
  monitoringRuleLogs?: ChangeLog[];
  monitoringRulePagination?: Pagination;
  organizations?: Organization[];
  organizationsPagination?: Pagination;
}

const initialState: TransactionMonitorinRuleState = {
  requestInProgress: false,
  isLoading: false,
};

export const transactionMonitoringRuleFormSlice = createSlice({
  name: 'transactionMonitoringRuleForm',
  initialState,
  reducers: {
    onSubmit: (
      state,
      action: PayloadAction<TransactionMonitoringRuleFormValues>,
    ) => {
      state.submitSuccess = false;
      state.formValues = action.payload;
      state.requestInProgress = true;
    },
    clearSettingStateState: (state) => {
      state.requestInProgress = false;
      state.formValues = undefined;
      state.submitSuccess = undefined;
      state.ruleId = undefined;
      state.monitoringRuleLogs = undefined;
      state.monitoringRulePagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        transactionMonitoringRulesApi.endpoints.createTransactionMonitoringRule
          .matchFulfilled,
        (state) => {
          state.requestInProgress = false;
          state.submitSuccess = true;
        },
      )
      .addMatcher(
        transactionMonitoringRulesApi.endpoints.createTransactionMonitoringRule
          .matchRejected,
        (state) => {
          state.requestInProgress = false;
        },
      )
      .addMatcher(
        transactionMonitoringRulesApi.endpoints.getTransactionMonitoringRule
          .matchPending,
        (state) => {
          state.isLoading = true;
        },
      )
      .addMatcher(
        transactionMonitoringRulesApi.endpoints.getTransactionMonitoringRule
          .matchFulfilled,
        (state, action: PayloadAction<{ data: TransactionMonitoringRule }>) => {
          const { data } = action.payload;

          state.isLoading = false;
          state.formValues = {
            clientType: data.clientType,
            transactionTypeId: data.transactionTypeId,
            transactionType: data.transactionType?.name,
            counterParty: data.counterParty,
            name: data.name,
            number: data.number,
            numberTo: data.numberTo || '',
            volumeFrom: data.volumeFrom,
            volumeTo: data.volumeTo,
            action: data.action,
            periodOfTimeInHours: data.periodOfTimeInHours,
            active: data.active,
            entity: data?.entity,
            paymentOrderType: data?.paymentOrderType,
            paymentType: data?.paymentType,
            clientRiskScoreRangeIds: data?.clientRiskScoreRangeIds || [],
            organizationIds: data?.organizationIds || [],
            currencyIds: data?.currencyIds || [],
            counterpartyCountryIds: data?.counterpartyCountryIds || [],
            counterpartyCountryRiskScoreRangeIds:
              data?.counterpartyCountryRiskScoreRangeIds || [],
            counterpartyBankCountryIds: data?.counterpartyBankCountryIds || [],
            counterpartyBankCountryRiskScoreRangeIds:
              data?.counterpartyBankCountryRiskScoreRangeIds || [],
            singleTransactionAmountFrom:
              data?.singleTransactionAmountFrom || '',
            singleTransactionAmountTo: data?.singleTransactionAmountTo || '',
          };
        },
      )
      .addMatcher(
        transactionMonitoringRulesApi.endpoints.getMonitoringRuleChangeLogs
          .matchFulfilled,
        (
          state,
          action: PayloadAction<Response<ChangeLog>> & {
            meta: { arg: { originalArgs: string } };
          },
        ) => {
          if (!action.meta.arg.originalArgs.includes('cursor')) {
            state.monitoringRuleLogs = action.payload.data?.edges?.map(
              (item) => item.node,
            );
          } else {
            const nextChangeLogs = ([] as ChangeLog[]).concat(
              state.monitoringRuleLogs as ChangeLog[],
            );

            action.payload.data.edges?.forEach((item) => {
              nextChangeLogs.push(item.node);
            });

            state.monitoringRuleLogs = nextChangeLogs;
          }

          state.monitoringRulePagination = action.payload.data?.pageInfo;
        },
      )
      .addMatcher(
        transactionMonitoringRulesApi.endpoints.getMonitoringRulesOrganization
          .matchFulfilled,
        (
          state,
          action: PayloadAction<Response<Organization>> & {
            meta: { arg: { originalArgs: string } };
          },
        ) => {
          if (!action.meta.arg.originalArgs.includes('cursor')) {
            state.organizations = action.payload.data?.edges?.map(
              (item) => item.node,
            );
          } else {
            const nextOrganizations = ([] as Organization[]).concat(
              state.organizations as Organization[],
            );

            action.payload.data.edges?.forEach((item) => {
              nextOrganizations.push(item.node);
            });

            state.organizations = nextOrganizations;
          }

          state.organizationsPagination = action.payload.data?.pageInfo;
        },
      )
      .addMatcher(
        transactionMonitoringRulesApi.endpoints.updateTransactionMonitoringRule
          .matchFulfilled,
        (state) => {
          state.requestInProgress = false;
          state.submitSuccess = true;
        },
      )
      .addMatcher(
        transactionMonitoringRulesApi.endpoints.updateTransactionMonitoringRule
          .matchRejected,
        (state) => {
          state.requestInProgress = false;
        },
      )
      .addMatcher(
        transactionMonitoringRulesApi.endpoints
          .toggleTransationMonitoringRulesActive.matchFulfilled,
        (state, action: PayloadAction<{ data: TransactionMonitoringRule }>) => {
          const { data } = action.payload;

          state.isLoading = false;
          state.formValues = {
            ...state.formValues,
            active: data.active,
          } as TransactionMonitoringRuleFormValues;
        },
      );
  },
});

export const { onSubmit, clearSettingStateState } =
  transactionMonitoringRuleFormSlice.actions;

export default transactionMonitoringRuleFormSlice.reducer;
